/* iter567 — Doctor Invite Landing Page.
 *
 * Public route /share/doctor/:token — лекар отваря линка, изпратен от пациент.
 * Three states:
 *   - Not logged in → CTA „Влез / Регистрирай се"
 *   - Logged in NOT as clinician → message + log out
 *   - Logged in as clinician with matching email → „Claim" button
 *
 * След successful claim → информация „Изчакай пациентът да одобри".
 */
import React, { useEffect, useState } from "react";
import axios from "axios";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Stethoscope, ArrowRight, CheckCircle, AlertCircle, Clock, Loader2 } from "lucide-react";
import { useAuth } from "@/App";
import { toast } from "sonner";

export default function DoctorInvitePage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { user, API } = useAuth();
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [claiming, setClaiming] = useState(false);
  const [claimedOk, setClaimedOk] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    axios
      .get(`${API}/doctor-share/invite/${token}`)
      .then((r) => {
        if (!cancelled) setInfo(r.data);
      })
      .catch((e) => {
        if (!cancelled)
          setError(e?.response?.data?.detail || "Поканата не е намерена.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [token, API]);

  const claim = async () => {
    setClaiming(true);
    const authToken =
      localStorage.getItem("nutramilo_access_token") ||
      sessionStorage.getItem("nutramilo_access_token");
    try {
      await axios.post(
        `${API}/doctor-share/invite/${token}/claim`,
        {},
        { headers: { Authorization: `Bearer ${authToken}` } }
      );
      setClaimedOk(true);
      toast.success("Готово. Пациентът ще одобри достъпа.");
    } catch (e) {
      toast.error(
        e?.response?.data?.detail || "Грешка при потвърждаване на поканата."
      );
    } finally {
      setClaiming(false);
    }
  };

  const role = (user?.role || "").toLowerCase();
  const isDoctor = ["clinician", "doctor", "admin"].includes(role);
  const emailMatches =
    user?.email?.toLowerCase().trim() ===
    (info?.doctor_email || "").toLowerCase().trim();

  return (
    <div className="min-h-screen bg-stone-50 flex items-center justify-center p-4" data-testid="doctor-invite-landing">
      <div className="w-full max-w-lg bg-white rounded-3xl shadow-lg border border-stone-200 overflow-hidden">
        <div className="relative px-6 py-6 sm:px-8 sm:py-8">
          {loading ? (
            <div className="text-center py-10">
              <Loader2 className="w-6 h-6 animate-spin text-stone-400 mx-auto" />
              <p className="mt-3 text-sm text-stone-500">Зареждам поканата…</p>
            </div>
          ) : error ? (
            <div className="text-center py-6">
              <AlertCircle className="w-10 h-10 text-rose-500 mx-auto mb-3" />
              <h2 className="text-lg font-bold text-stone-900">Грешка</h2>
              <p className="mt-2 text-sm text-stone-600">{error}</p>
              <Link
                to="/"
                className="mt-4 inline-block text-sm text-violet-600 hover:underline"
              >
                Към началната страница
              </Link>
            </div>
          ) : claimedOk ? (
            <div className="text-center py-6" data-testid="claim-success">
              <CheckCircle className="w-12 h-12 text-emerald-500 mx-auto mb-3" />
              <h2 className="text-xl font-bold text-stone-900">Потвърдено</h2>
              <p className="mt-2 text-sm text-stone-600 leading-relaxed">
                Изчакай пациентът да одобри достъпа от своя профил. Ще видиш
                {" "}<strong>{info.patient_name}</strong>{" "}в раздел „Споделени пациенти&rdquo; в твоя doctor dashboard.
              </p>
              <button
                type="button"
                onClick={() => navigate("/doctor")}
                className="mt-5 inline-flex items-center gap-1.5 px-5 py-2.5 rounded-full bg-stone-900 text-white font-semibold text-sm hover:bg-stone-800 transition-colors"
              >
                Към Doctor Dashboard
                <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          ) : (
            <>
              <div className="flex items-center gap-3 mb-5">
                <div className="w-12 h-12 rounded-2xl bg-violet-100 flex items-center justify-center shrink-0">
                  <Stethoscope className="w-6 h-6 text-violet-700" />
                </div>
                <div>
                  <h1 className="text-xl font-bold text-stone-900">Покана от пациент</h1>
                  <p className="text-sm text-stone-500">Read-only достъп до здравни данни</p>
                </div>
              </div>

              <div className="rounded-2xl bg-stone-50 border border-stone-200 p-4 space-y-2 text-sm">
                <div>
                  <span className="text-stone-500">Пациент:</span>{" "}
                  <strong className="text-stone-900">{info.patient_name || "(без име)"}</strong>
                </div>
                <div>
                  <span className="text-stone-500">Изпратено до:</span>{" "}
                  <strong className="text-stone-900">{info.doctor_email}</strong>
                </div>
                {info.note && (
                  <div className="text-stone-700 italic mt-2">„{info.note}&rdquo;</div>
                )}
                <div className="flex items-center gap-1 text-xs text-stone-500 mt-2">
                  <Clock className="w-3 h-3" />
                  Изтича: {new Date(info.expires_at).toLocaleString("bg-BG")}
                </div>
                {info.status !== "invited" && (
                  <div className="mt-2 px-2 py-1 rounded-md bg-amber-100 border border-amber-200 text-amber-900 text-xs">
                    Статус: {info.status}
                  </div>
                )}
              </div>

              <div className="mt-5">
                {!user ? (
                  <div className="space-y-3">
                    <p className="text-sm text-stone-700 leading-relaxed">
                      За да достъпиш данните, влез в Nutramilo с doctor акаунт
                      (email <strong>{info.doctor_email}</strong>) или се регистрирай.
                    </p>
                    <div className="flex flex-wrap gap-2">
                      <Link
                        to={`/login?next=/share/doctor/${token}`}
                        data-testid="invite-login"
                        className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-full bg-stone-900 text-white font-semibold text-sm hover:bg-stone-800 transition-colors"
                      >
                        Влез
                      </Link>
                      <Link
                        to={`/register?role=clinician&next=/share/doctor/${token}`}
                        data-testid="invite-register"
                        className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-full bg-violet-100 hover:bg-violet-200 text-violet-800 font-semibold text-sm transition-colors"
                      >
                        Регистрирай се като лекар
                      </Link>
                    </div>
                  </div>
                ) : !isDoctor ? (
                  <div className="p-3 rounded-xl bg-amber-50 border border-amber-200 text-sm text-amber-900">
                    Този линк е за лекари. Твоят акаунт е регистриран като пациент.
                    Излез и влез отново с doctor акаунт ако имаш такъв.
                  </div>
                ) : !emailMatches ? (
                  <div className="p-3 rounded-xl bg-rose-50 border border-rose-200 text-sm text-rose-900">
                    Логнатият email <strong>{user.email}</strong> не съвпада с
                    адреса на поканата (<strong>{info.doctor_email}</strong>).
                    Излез и влез с правилния email.
                  </div>
                ) : info.status !== "invited" ? (
                  <div className="p-3 rounded-xl bg-stone-100 border border-stone-200 text-sm text-stone-700">
                    Тази покана вече е обработена (статус: {info.status}).
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={claim}
                    disabled={claiming}
                    data-testid="invite-claim"
                    className="w-full inline-flex items-center justify-center gap-1.5 px-5 py-3 rounded-full bg-stone-900 text-white font-semibold text-sm hover:bg-stone-800 disabled:opacity-60 disabled:cursor-wait transition-colors"
                  >
                    {claiming ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <CheckCircle className="w-4 h-4" />
                    )}
                    {claiming ? "Потвърждавам…" : "Приеми поканата"}
                  </button>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
